import Checkbox from "@components/base/checkbox/checkbox";
import { useEffect, useMemo, useRef, useState } from "react";
import ReactCountryFlag from "react-country-flag";
import { ChevronDown } from "react-feather";

const CustomSelect = ({ options = [], value = null, onChange = () => null, isMulti = false, disabled = false, placeholder = "Select...", className = "", maxLimit = 3, isCountry = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  const optionsToUse = useMemo(() => {
    return options.map((opt) => (typeof opt === "object" ? opt : { value: opt, label: String(opt) }));
  }, [options]);

  const filteredOptions = useMemo(() => {
    const trimmedSearchTerm = searchTerm.trim();
    if (trimmedSearchTerm === "") return optionsToUse;
    return optionsToUse.filter((option) => {
      const label = typeof option.label === "string" ? option.label : option.labelValue;
      return label.toLowerCase().includes(trimmedSearchTerm.toLowerCase()) || option.value.toLowerCase().includes(trimmedSearchTerm.toLowerCase());
    });
  }, [optionsToUse, searchTerm]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const getKey = (opt) => opt?.value || opt?.code || opt;

  const handleOptionSelect = (option) => {
    if (isMulti) {
      const currentArray = Array.isArray(value) ? value : [];
      const isSelected = currentArray.some((item) => getKey(item) === getKey(option));

      let newValue;
      if (isSelected) {
        newValue = currentArray.filter((item) => getKey(item) !== getKey(option));
      } else {
        if (currentArray.length >= maxLimit) return; // 🚀 Prevent selecting more than maxLimit
        newValue = [...currentArray, option];
      }
      onChange(newValue);
    } else {
      onChange(option);
      setIsOpen(false);
      setSearchTerm("");
    }
  };

  const handleRemoveValue = (optionToRemove) => {
    if (isMulti) {
      const currentArray = Array.isArray(value) ? value : [];
      const newValue = currentArray.filter((item) => getKey(item) !== getKey(optionToRemove));
      onChange(newValue);
    } else {
      onChange(null);
    }
  };

  const renderValue = () => {
    if (isMulti) {
      const values = Array.isArray(value) ? value : [];
      if (values.length === 0) {
        return <span className="tw-text-gray-400 tw-text-sm">{placeholder}</span>;
      }
      return values.map((opt) => {
        const label = opt.label.slice(0, 15) + (opt.label.length > 15 ? "..." : "");
        return (
          <div key={getKey(opt)} className="tw-flex tw-items-center tw-gap-1 tw-bg-primary-purple-50 tw-pl-4 pr-2  tw-py-1 tw-rounded-2xl tw-text-sm" onClick={(e) => e.stopPropagation()}>
            <span className="tw-text-xs tw-flex tw-items-center tw-gap-1">
              {isCountry && <ReactCountryFlag countryCode={opt.value} svg style={{ width: "16px", height: "16px" }} />}
              {label}
            </span>
            <span
              className="tw-cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                handleRemoveValue(opt);
              }}>
              <svg xmlns="http://www.w3.org/2000/svg" height="16px" viewBox="0 -960 960 960" width="16px" fill="#101828">
                <path d="m291-240-51-51 189-189-189-189 51-51 189 189 189-189 51 51-189 189 189 189-51 51-189-189-189 189Z" />
              </svg>
            </span>
          </div>
        );
      });
    } else {
      if (!value) return <span className="tw-text-gray-400 tw-text-sm">{placeholder}</span>;
      const label = value.label;
      return (
        <div className="tw-text-sm tw-font-medium tw-flex tw-items-center tw-gap-2 tw-line-clamp-1">
          {isCountry && <ReactCountryFlag countryCode={value.value} svg style={{ width: "16px", height: "16px" }} />}
          <span className="tw-flex tw-flex-shrink-0">{label}</span>
        </div>
      );
    }
  };

  return (
    <div className={`tw-w-full ${className}`}>
      <div className="tw-relative" ref={dropdownRef}>
        <div className={`tw-h-11 tw-w-full tw-px-2 tw-py-1 tw-border tw-rounded-lg tw-bg-white tw-flex tw-items-center tw-justify-between ${disabled ? "tw-opacity-50 tw-cursor-not-allowed tw-bg-gray-50" : isOpen ? "tw-border-primary-purple-500 tw-ring-1 tw-ring-primary-purple-500" : "hover:tw-border-primary-purple-500"}`} onClick={() => !disabled && setIsOpen((prev) => !prev)}>
          <div className="tw-flex tw-gap-2 tw-whitespace-nowrap">{renderValue()}</div>
          <ChevronDown className={`tw-w-5 tw-h-5 tw-text-gray-400 transition-transform ${isOpen ? "tw-rotate-180" : ""}`} />
        </div>

        {isOpen && !disabled && (
          <div className="tw-absolute tw-z-20 tw-w-full tw-mt-1 tw-bg-white tw-border tw-rounded-lg tw-shadow-lg">
            <div className="tw-p-3 tw-border-b">
              <div className="tw-relative">
                <input ref={searchInputRef} type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search..." className="tw-w-full tw-text-sm tw-pl-3 tw-pr-8 tw-py-2 tw-border tw-rounded-md" />
                {searchTerm && (
                  <span onClick={() => setSearchTerm("")} className="tw-cursor-pointer tw-absolute tw-right-3 tw-top-2 tw-text-gray-400 hover:tw-text-gray-600">
                    <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#101828">
                      <path d="m291-240-51-51 189-189-189-189 51-51 189 189 189-189 51 51-189 189 189 189-51 51-189-189-189 189Z" />
                    </svg>
                  </span>
                )}
              </div>
            </div>

            {/* Options */}
            <div className="tw-max-h-60 tw-overflow-y-auto">
              {filteredOptions.length === 0 ? (
                <div className="tw-p-4 tw-text-gray-500 tw-text-center tw-text-sm">No options found</div>
              ) : (
                filteredOptions.map((option) => {
                  const optionValue = getKey(option);
                  const isSelected = isMulti ? Array.isArray(value) && value.some((item) => getKey(item) === optionValue) : value && getKey(value) === optionValue;

                  const label = option.label || option.name || option.value;
                  const multiSelectDisable = isMulti && !isSelected && Array.isArray(value) && value.length >= maxLimit;

                  return (
                    <div key={optionValue} className={`tw-flex tw-text-sm tw-items-center tw-gap-3 tw-px-4 tw-py-2.5 ${multiSelectDisable ? "tw-cursor-not-allowed tw-opacity-50" : "tw-cursor-pointer hover:tw-bg-gray-50"}`} onClick={() => !multiSelectDisable && handleOptionSelect(option)}>
                      {isMulti && (
                        <div onClick={(e) => e.stopPropagation()} className="tw-leading-none">
                          <Checkbox disabled={multiSelectDisable} id={optionValue} name={optionValue} checked={isSelected} onChange={() => !multiSelectDisable && handleOptionSelect(option)} className="tw-w-4.5 tw-h-4.5 tw-text-gray-900 tw-border-gray-300 tw-rounded" />
                        </div>
                      )}
                      <span className="tw-flex tw-items-center tw-gap-2 tw-text-sm">
                        {isCountry && <ReactCountryFlag countryCode={option.value} svg style={{ width: "16px", height: "16px" }} />}
                        {label}
                      </span>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomSelect;
